import { Spacing, Text } from '@vkontakte/vkui';
import animation from 'img/quota-landing/skipped-payment-video.webm';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { skippedPaymentTrials14d } from 'reactApp/appHelpers/featuresHelpers/features/skippedPayments';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { RootState } from 'reactApp/store';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getPeriodName, getTariffProductsByPeriods, isMonthPeriod } from 'reactApp/utils/Period';

import styles from './SkippedPaymentFinalDialog.css';
import type { SkippedPaymentFinalDialogProps } from './SkippedPaymentFinalDialog.types';

const sendRadar = (action: 'click' | 'show' | 'close') => {
    sendPaymentGa({
        eventCategory: ECategoryGa.modal,
        action,
        name: 'mnz_trial14_in_abandoned_payment',
    });
};

export const SkippedPaymentFinalDialog = memo<SkippedPaymentFinalDialogProps>(({ onClose, product }) => {
    const dispatch = useDispatch();

    const videoRef = useRef<HTMLVideoElement | null>(null);

    const space = product.space.space;
    const productType = product?.isFamily ? 'family' : 'regular';
    const trialIdBySpace = skippedPaymentTrials14d[productType]?.[space];
    const trial = useSelector((state: RootState) => ProductsSelectors.getTariffById(state, trialIdBySpace));

    useEffect(() => {
        sendRadar('show');
        emitAnalyticEvent(AnalyticEventNames.SKIPPED_PAYMENT_MODAL_14D_SHOW);
        videoRef?.current?.play();
    }, []);

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose?.();
    }, [onClose]);

    const handleClick = useCallback(() => {
        const { yearProduct: yearTrial, monthProduct: monthTrial } = getTariffProductsByPeriods(trial?.products || []);
        const productId = isMonthPeriod(product.period) ? monthTrial.id : yearTrial.id;
        const trialPeriod = isMonthPeriod(product.period) ? monthTrial.trialPeriod : yearTrial.trialPeriod;

        dispatch(
            openTariffBuy({
                productId,
                checkAnonymous: false,
                isMobile: false,
                paySource: 'trial14_in_abandoned_payment',
                title: `Подписка Mail Space на ${getPeriodName(trialPeriod || '', false, false, true)} бесплатно`,
                hideOtherTariffs: true,
            })
        );

        onClose?.();
        sendRadar('click');
        emitAnalyticEvent(AnalyticEventNames.SKIPPED_PAYMENT_MODAL_14D_CLICK);
    }, [dispatch, product, trial, onClose]);

    if (!trial) {
        return null;
    }

    return (
        <Dialog open id="skipped-payment-final-modal" closable size="big" onCancel={handleClose} className={styles.root} bigCloseIcon>
            <video ref={videoRef} autoPlay muted playsInline loop className={styles.video}>
                <source src={animation} type="video/webm" />
            </video>
            <div className={styles.content}>
                <Text className={styles.title}>Успеть ухватить!</Text>
                <Spacing size={12} />
                <Text className={styles.text}>
                    Подпиской можно пользоваться <span>14 дней бесплатно</span> — как раз хватит времени,
                    <br />
                    чтобы переместить фото из галереи в Облако и разгрузить телефон
                </Text>
                <Spacing size={32} />
                <Button primary sizeMode={ButtonSizeMode.big} theme="vk" className={styles.button} onClick={handleClick}>
                    Забрать {product.space.value}
                </Button>
            </div>
        </Dialog>
    );
});

SkippedPaymentFinalDialog.displayName = 'SkippedPaymentFinalDialog';
